import UserRequestCard from "@/components/Cards/UserRequestCard/UserRequestCard";
import HeaderComponent from "@/components/Header/Header";
import React, { useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag, Modal, Input, Button } from "antd";
import { X, Check } from "lucide-react";
import { tableBody_2 as tableBody2, tableHeader2 } from "@/styles/TableStyles";
import { useQuery } from "@tanstack/react-query";
import { responderCompanies } from "@/services/queries/responderCompanyQueries";
import { Skeleton } from "primereact/skeleton";
import { useProcessCompanyRequest } from "@/hooks/useResponderCompany";

const { TextArea } = Input;

type UserRequestStatus = "Pending" | "Approved" | "Rejected";

interface UserRequest {
  id: string; // GUID from API
  company: string;
  firstName: string;
  lastName: string;
  status: UserRequestStatus;
  number: string;
  email: string;
}

// Convert numeric enum to string for display
const convertStatusToString = (status: number | string): UserRequestStatus => {
  if (typeof status === "string") return status as UserRequestStatus;

  switch (status) {
    case 0:
      return "Pending";
    case 1:
      return "Approved";
    case 2:
      return "Rejected";
    default:
      return "Pending";
  }
};

const getStatusColors = (status: UserRequestStatus) => {
  switch (status) {
    case "Pending":
      return { bgColor: "#FEF3C7", textColor: "#D97706" };
    case "Approved":
      return { bgColor: "#D1FADF", textColor: "#179717" };
    case "Rejected":
      return { bgColor: "#FEE2E2", textColor: "#D92D20" };
    default:
      return { bgColor: "#FEF3C7", textColor: "#D97706" };
  }
};

const MangeUserRequests = () => {
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] =
    useState<IResponderCompanyAssociationTableDTO | null>(null);
  const [rejectionReason, setRejectionReason] = useState<string>("");

  // Fetch all associations from API
  const {
    data: apiResponse,
    isLoading,
    error,
  } = useQuery(responderCompanies.allAssociations(pageNumber, rows));

  // Fetch association stats
  const { data: statsResponse } = useQuery(
    responderCompanies.associationStats(),
  );

  // Mutation hook for approve/reject
  const { mutate: processRequest, isPending: isProcessing } =
    useProcessCompanyRequest();

  // Map API response to UserRequest interface
  const userRequests: UserRequest[] =
    apiResponse?.data?.map((association) => ({
      id: association.id,
      company: association.responderCompanyName,
      firstName: association.firstname,
      lastName: association.lastName,
      status: convertStatusToString(association.status),
      number: association.phoneNumber,
      email: association.emailAddress,
    })) || [];

  // Get counts from stats API
  const pendingCount = statsResponse?.data?.pending || 0;
  const approvedCount = statsResponse?.data?.approved || 0;
  const rejectedCount = statsResponse?.data?.rejected || 0;

  const onPage = (event: any) => {
    setFirst(event.first);
    setRows(event.rows);
    setPageNumber(Math.floor(event.first / event.rows) + 1);
  };

  const statusBodyTemplate = (rowData: UserRequest) => {
    const color = getStatusColors(rowData.status);
    return (
      <Tag color={color.bgColor} className="px-2.5 py-0.5 rounded">
        <p
          className="flex justify-center items-center mx-0 my-0"
          style={{ color: color.textColor }}
        >
          {rowData.status}
        </p>
      </Tag>
    );
  };

  const handleApproveClick = (rowData: UserRequest) => {
    // Find the full association data from API response
    const association = apiResponse?.data?.find((a) => a.id === rowData.id);
    if (association) {
      setSelectedRequest(association);
      setIsApproveModalOpen(true);
    }
  };

  const handleRejectClick = (rowData: UserRequest) => {
    // Find the full association data from API response
    const association = apiResponse?.data?.find((a) => a.id === rowData.id);
    if (association) {
      setSelectedRequest(association);
      setIsRejectModalOpen(true);
    }
  };

  const actionBodyTemplate = (rowData: UserRequest) => {
    const isPending = rowData.status === "Pending";
    const btnBase =
      "flex items-center justify-center w-8 h-8 " +
      "rounded-md border-none transition-colors";
    const disabledClass = !isPending
      ? "opacity-50 cursor-not-allowed"
      : "cursor-pointer";

    return (
      <div className="flex flex-row gap-2">
        <button
          type="button"
          className={`${btnBase} ${disabledClass} bg-red-50 text-red-500 hover:bg-red-100`}
          onClick={() => isPending && handleRejectClick(rowData)}
          disabled={!isPending}
        >
          <X className="w-4 h-4" />
        </button>
        <button
          type="button"
          className={`${btnBase} ${disabledClass} bg-green-50 text-green-500 hover:bg-green-100`}
          onClick={() => isPending && handleApproveClick(rowData)}
          disabled={!isPending}
        >
          <Check className="w-4 h-4" />
        </button>
      </div>
    );
  };

  // Handle loading state
  if (isLoading) {
    return (
      <div className="flex flex-col w-full h-full bg-backgroundColor">
        <HeaderComponent title="Manage User Requests" />
        <div className="flex flex-col w-full h-full p-4 gap-4">
          {/* Skeleton for cards */}
          <div className="flex flex-row w-full gap-2">
            {[1, 2, 3].map((index) => (
              <div
                key={index}
                className="flex flex-col bg-white rounded-lg p-4 w-full shadow-sm"
              >
                <Skeleton width="60%" height="1.5rem" className="mb-2" />
                <Skeleton width="40%" height="3rem" className="mb-2" />
                <Skeleton width="80%" height="1rem" className="mb-2" />
                <Skeleton width="30%" height="1.5rem" />
              </div>
            ))}
          </div>

          {/* Skeleton for table */}
          <div className="bg-white rounded-lg p-4 mx-3">
            <Skeleton width="100%" height="3rem" className="mb-3" />
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((row) => (
              <Skeleton key={row} width="100%" height="3rem" className="mb-2" />
            ))}
            <Skeleton width="100%" height="3rem" className="mt-3" />
          </div>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="flex flex-col w-full h-full bg-backgroundColor">
        <HeaderComponent title="Manage User Requests" />
        <div className="flex items-center justify-center h-full">
          <p className="text-lg text-red-500">
            Error loading user requests. Please try again later.
          </p>
        </div>
      </div>
    );
  }

  const handleApprove = () => {
    if (!selectedRequest) {
      return;
    }

    processRequest(
      {
        Id: selectedRequest.userId,
        CompanyName: selectedRequest.responderCompanyName,
        ResponderCompanyId: selectedRequest.responderCompanyId,
        Status: 1, // Approved
        FirstName: selectedRequest.firstname,
        LastName: selectedRequest.lastName,
        Email: selectedRequest.emailAddress,
        PhoneNumber: selectedRequest.phoneNumber,
        SubscriptionTypeId: selectedRequest.subscriptionTypeId,
      },
      {
        onSuccess: () => {
          setIsApproveModalOpen(false);
          setSelectedRequest(null);
        },
      },
    );
  };

  const handleCancelApprove = () => {
    setIsApproveModalOpen(false);
    setSelectedRequest(null);
  };

  const handleReject = () => {
    if (!selectedRequest) {
      return;
    }

    processRequest(
      {
        Id: selectedRequest.userId,
        CompanyName: selectedRequest.responderCompanyName,
        ResponderCompanyId: selectedRequest.responderCompanyId,
        Status: 2, // Rejected
        FirstName: selectedRequest.firstname,
        LastName: selectedRequest.lastName,
        Email: selectedRequest.emailAddress,
        PhoneNumber: selectedRequest.phoneNumber,
        Reason: rejectionReason || undefined,
      },
      {
        onSuccess: () => {
          setIsRejectModalOpen(false);
          setSelectedRequest(null);
          setRejectionReason("");
        },
      },
    );
  };

  const handleCancelReject = () => {
    setIsRejectModalOpen(false);
    setSelectedRequest(null);
    setRejectionReason("");
  };

  return (
    <>
      {/* Approve Modal */}
      <Modal
        title="Approve user request from company"
        open={isApproveModalOpen}
        onCancel={handleCancelApprove}
        footer={[
          <Button
            key="cancel"
            type="primary"
            ghost
            onClick={handleCancelApprove}
            size="large"
            className="w-3"
            disabled={isProcessing}
          >
            Cancel
          </Button>,
          <Button
            key="approve"
            type="primary"
            onClick={handleApprove}
            loading={isProcessing}
            size="large"
            className="w-3 bg-[#1757a2] shadow-none"
          >
            Approve
          </Button>,
        ]}
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            You are about to approve this user from the responder company. Once
            approved, please note that the user's existing subscription will be
            canceled, and they will then adopt the responder's companies
            selected subscription.
          </p>

          {selectedRequest && (
            <div className="bg-gray-50 p-4 rounded-md space-y-2">
              <p className="text-sm">
                <strong>User:</strong> {selectedRequest.firstname}{" "}
                {selectedRequest.lastName}
              </p>
              <p className="text-sm">
                <strong>Email:</strong> {selectedRequest.emailAddress}
              </p>
              <p className="text-sm">
                <strong>Company:</strong> {selectedRequest.responderCompanyName}
              </p>
            </div>
          )}
        </div>
      </Modal>

      {/* Reject Modal */}
      <Modal
        title="Reject user request from company"
        open={isRejectModalOpen}
        onCancel={handleCancelReject}
        footer={[
          <Button
            key="cancel"
            type="primary"
            ghost
            onClick={handleCancelReject}
            size="large"
            className="w-3"
            disabled={isProcessing}
          >
            Cancel
          </Button>,
          <Button
            key="reject"
            type="primary"
            onClick={handleReject}
            loading={isProcessing}
            disabled={!rejectionReason.trim() || isProcessing}
            size="large"
            className="w-3 bg-[#1757a2] shadow-none"
          >
            Reject
          </Button>,
        ]}
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            You are about to decline this Responder company&apos;s request to
            add this user. Please provide a reason below that will be sent to
            the Responder admin.
          </p>

          {selectedRequest && (
            <div className="bg-gray-50 p-4 rounded-md space-y-2">
              <p className="text-sm">
                <strong>User:</strong> {selectedRequest.firstname}{" "}
                {selectedRequest.lastName}
              </p>
              <p className="text-sm">
                <strong>Email:</strong> {selectedRequest.emailAddress}
              </p>
              <p className="text-sm">
                <strong>Company:</strong> {selectedRequest.responderCompanyName}
              </p>
            </div>
          )}

          <div className="space-y-3 mb-6">
            <label className="block text-sm font-medium text-gray-700">
              Provide a reason for rejection
            </label>
            <TextArea
              placeholder="Enter reason for rejection (optional)"
              rows={4}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              maxLength={500}
              showCount
            />
          </div>
        </div>
      </Modal>

      <div className="flex flex-col w-full h-full bg-backgroundColor">
        <HeaderComponent title="Manage User Requests" />
        <div className="flex flex-col w-full h-full p-4 gap-4">
          <div className="flex flex-row w-full gap-2">
            <UserRequestCard
              title="Pending Users"
              count={pendingCount}
              description="Number of users with a pending status"
              badgeText="Pending"
              badgeVariant="pending"
            />
            <UserRequestCard
              title="Approved Users"
              count={approvedCount}
              description="Number of approved users to join the company"
              badgeText="Approved"
              badgeVariant="approved"
            />
            <UserRequestCard
              title="Rejected Users"
              count={rejectedCount}
              description="Number of rejected users"
              badgeText="Rejected"
              badgeVariant="rejected"
            />
          </div>

          <DataTable
            value={userRequests}
            totalRecords={userRequests.length}
            paginator
            first={first}
            rows={rows}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPage={onPage}
            dataKey="id"
            emptyMessage="No user requests found."
            rowHover
            scrollable
            scrollHeight="45rem"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} requests"
            stateStorage="session"
            stateKey="dt-state-user-requests-table"
            className="mx-3"
          >
            <Column
              field="company"
              header="Company"
              headerStyle={tableHeader2}
              bodyStyle={tableBody2}
              className="bg-transparent"
            />
            <Column
              field="firstName"
              header="First Name"
              headerStyle={tableHeader2}
              bodyStyle={tableBody2}
              className="bg-transparent"
            />
            <Column
              field="lastName"
              header="Last Name"
              headerStyle={tableHeader2}
              bodyStyle={tableBody2}
              className="bg-transparent"
            />
            <Column
              field="status"
              header="Status"
              headerStyle={tableHeader2}
              bodyStyle={tableBody2}
              className="bg-transparent"
              body={statusBodyTemplate}
            />
            <Column
              field="number"
              header="Number"
              headerStyle={tableHeader2}
              bodyStyle={tableBody2}
              className="bg-transparent"
            />
            <Column
              field="email"
              header="Email"
              headerStyle={tableHeader2}
              bodyStyle={tableBody2}
              className="bg-transparent"
            />
            <Column
              header="CTA"
              exportable={false}
              body={actionBodyTemplate}
              headerStyle={tableHeader2}
              bodyStyle={tableBody2}
              className="bg-transparent"
            />
          </DataTable>
        </div>
      </div>
    </>
  );
};

export default MangeUserRequests;
